<template>
    <div class="px-5 uses-tailwind">
        <div class="grid items-end grid-cols-12 gap-4">
            <div
                class="col-span-full sm:col-span-6 lg:col-span-4 xl:col-span-3"
            >
                <div class="flex flex-col gap-2">
                    <div class="flex items-center gap-2">
                        <ui5-label
                            class="text-3xl"
                            for="pleating-list-id"
                            required
                        >
                            {{
                                trans('actions.scan_subject', {
                                    subject: trans(
                                        'pleating_lists.pleating_list'
                                    ).toLowerCase(),
                                })
                            }}:
                        </ui5-label>
                        <spinner
                            v-if="state.fetchingData"
                            class="h-6"
                        />
                    </div>
                    <ui5-input
                        class="text-3xl bg-white h-[70px] w-full m-0"
                        id="pleating-list-id"
                        type="search"
                        show-clear-icon="true"
                        :value="scannedPleatingListId || ''"
                        @change="scannedPleatingListId = $event.target.value"
                        :placeholder="
                            trans('actions.scan_subject', {
                                subject: trans(
                                    'pleating_lists.pleating_list'
                                ).toLowerCase(),
                            })
                        "
                    />
                </div>
            </div>
            <div
                class="col-span-full sm:col-span-6 lg:col-span-4 xl:col-span-3 sm:col-start-7 lg:col-start-9 xl:col-start-10"
            >
                <div class="flex items-center justify-end gap-5">
                    <spinner
                        v-if="state.savingData"
                        class="h-[50px]"
                    />
                    <button
                        :disabled="
                            !areAllProductsFinished ||
                            state.savingData ||
                            (pleatingList && pleatingList.done)
                        "
                        @click="onFinished"
                        class="bg-primary h-[70px] hover:bg-primary-dark focus:bg-primary-dark transition-all cursor-pointer text-3xl text-white p-4 text-center rounded-lg disabled:opacity-20"
                    >
                        {{ trans('actions.finish') }}
                    </button>
                </div>
            </div>
        </div>
        <div
            v-if="pleatingList && (pleatingList.done || areAllProductsFinished)"
            class="mt-8"
        >
            <div
                class="text-lg text-lime-600"
                v-if="pleatingList.done && areAllProductsFinished"
            >
                {{ trans('pleating_lists.pleating_list_done') }}
            </div>
            <div
                class="text-lg text-orange-600"
                v-if="!pleatingList.done && areAllProductsFinished"
            >
                {{
                    trans('pleating_lists.pleating_list_must_be_marked_as_done')
                }}
            </div>
        </div>
        <table class="w-full mt-8 border-collapse table-fixed">
            <thead class="border-b border-slate-400">
                <tr>
                    <th class="p-4 font-bold text-lg w-[300px] text-left">
                        {{
                            trans('pleating_lists.products_table.headers.image')
                        }}
                    </th>
                    <th class="p-4 text-lg font-bold text-left">
                        {{
                            trans(
                                'pleating_lists.products_table.headers.description'
                            )
                        }}
                    </th>
                    <th class="p-4 font-bold text-lg w-[150px] text-center">
                        {{
                            trans(
                                'pleating_lists.products_table.headers.finished'
                            )
                        }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="state.fetchingData">
                    <td
                        class="p-8"
                        colspan="3"
                    >
                        <div class="flex justify-center">
                            <spinner class="h-6" />
                        </div>
                    </td>
                </tr>
                <tr
                    v-if="
                        !state.fetchingData &&
                        pleatingList &&
                        products.length == 0
                    "
                >
                    <td
                        class="p-8"
                        colspan="3"
                    >
                        <div class="text-center">
                            {{
                                trans(
                                    'pleating_lists.products_table.no_products'
                                )
                            }}
                        </div>
                    </td>
                </tr>
                <template
                    v-if="
                        !state.fetchingData &&
                        pleatingList &&
                        products.length > 0
                    "
                >
                    <tr
                        v-for="product in products"
                        :key="'product.' + product.id"
                        class="odd:bg-slate-100"
                    >
                        <td class="p-4">
                            <img
                                :src="product.imageSrc"
                                :alt="product.number"
                                @click="openImageModal(product)"
                                class="block object-contain object-center aspect-video bg-slate-200 cursor-zoom-in"
                            />
                        </td>
                        <td class="p-4">
                            <div class="flex-col gap-2 text-xl">
                                <p class="text-3xl font-bold !mb-1">
                                    {{ product.number }} ({{
                                        trans('products.count_colon_value', {
                                            count: product.count,
                                        })
                                    }})
                                </p>
                                <p>
                                    {{ product.customerName }}
                                </p>
                                <p>
                                    {{ product.reference }}
                                </p>
                                <p v-if="product.color">
                                    {{
                                        `${product.color.ralNumber} ${product.color.ralType}`
                                    }}
                                </p>
                                <div class="mt-4">
                                    <p v-if="getCreationFilter(product)">
                                        {{
                                            getDataFieldLabel(
                                                getCreationFilter(product)
                                                    .dataField
                                            )
                                        }}:
                                        {{
                                            parseFloat(
                                                getCreationFilter(product).value
                                            ).toFixed(2)
                                        }}
                                        mm
                                    </p>
                                    <p v-if="getDataFieldLength(product)">
                                        {{
                                            getDataFieldLabel(
                                                getDataFieldLength(product)
                                                    .dataField
                                            )
                                        }}:
                                        {{
                                            parseFloat(
                                                getDataFieldLength(product)
                                                    .value
                                            ).toFixed(2)
                                        }}
                                        mm
                                    </p>
                                    <p v-if="getDataFieldUnfoldment(product)">
                                        {{
                                            getDataFieldLabel(
                                                getDataFieldUnfoldment(product)
                                                    .dataField
                                            )
                                        }}:
                                        {{
                                            parseFloat(
                                                getDataFieldUnfoldment(product)
                                                    .value
                                            ).toFixed(2)
                                        }}
                                        mm
                                    </p>
                                </div>
                            </div>
                        </td>
                        <td class="p-4">
                            <div class="flex items-center justify-center">
                                <input
                                    type="checkbox"
                                    :checked="isProductFinished(product)"
                                    @change="
                                        toggleProductFinished($event, product)
                                    "
                                />
                            </div>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
        <modal
            v-if="imageModal.product"
            :show="imageModal.show"
            :loading="imageModal.loading"
            :actions="imageModal.actions"
            @close="imageModal.show = false"
            :title="imageModal.product.number"
        >
            <img
                :src="imageModal.product.imageSrc"
                :alt="imageModal.product.number"
                @click="imageModal.show = false"
                class="block aspect-video bg-slate-200 cursor-zoom-out max-w-full w-[1200px]"
            />
        </modal>
    </div>
</template>

<script>
import IsDebugMixin from '../mixins/is-debug';
import pleatingListApi from '../api/pleatingList';
import Spinner from '../components/loader/Spinner';
import Modal from '../components/modals/Modal';
import { showInfoMessage, showErrorMessage } from '../dialogs';
import _ from 'lodash';
import localization from '../mixins/localization';

export default {
    mixins: [IsDebugMixin, localization],

    components: { Spinner, Modal },

    data() {
        return {
            scannedPleatingListId: null,
            pleatingList: null,
            state: {
                fetchingData: false,
                savingData: false,
            },
            imageModal: {
                show: false,
                loading: false,
                product: null,
                actions: [],
            },
        };
    },

    computed: {
        areAllProductsFinished() {
            return (
                this.pleatingList &&
                this.products.length &&
                this.products.every((product) =>
                    this.isProductFinished(product)
                )
            );
        },

        products() {
            if (!this.pleatingList || !this.pleatingList.records) {
                return [];
            }

            return this.pleatingList.records.reduce((acc, record) => {
                if (!record.sawlines) {
                    return acc;
                }

                record.sawlines.forEach((sawline) => {
                    if (!sawline.product) {
                        return;
                    }

                    if (acc.find((x) => x.id === sawline.product.id)) {
                        return;
                    }

                    const product = sawline.product;

                    product.imageSrc =
                        Config.url +
                        '/product/customImage/?productId=' +
                        product.id +
                        '&random=' +
                        Math.random();

                    acc.push(product);
                });

                return acc;
            }, []);
        },
    },

    watch: {
        scannedPleatingListId: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.fetchDataDebounced(this);
                }
            },
        },
    },

    mounted() {
        this.scannedPleatingListId = this.$route.query.pleatingListId;

        this.listenForBarCodeScannerEvents();
    },

    methods: {
        capitalize: _.capitalize,

        listenForBarCodeScannerEvents() {
            this.$bus.$on('barcode-scan', (data) => {
                if (data.startsWith('PL')) {
                    this.scannedPleatingListId = data;
                    this.fetchData();
                }
            });
        },

        fetchDataDebounced: _.debounce((self) => {
            self.fetchData();
        }, 500),

        fetchData() {
            this.state.fetchingData = true;

            this.reset(false);

            if (!this.scannedPleatingListId) {
                this.state.fetchingData = false;

                return;
            }

            pleatingListApi
                .fetchDataForPleatingWorkstation(this.scannedPleatingListId)
                .then((response) => {
                    if (response.status !== 200 || !response.data.data) {
                        throw new Error(this.trans('pleating_lists.not_found'));
                    }

                    this.pleatingList = response.data.data;
                })
                .catch((error) => {
                    showErrorMessage(this.trans('pleating_lists.not_found'));
                })
                .finally(() => {
                    this.state.fetchingData = false;
                });
        },

        onFinished() {
            // KEY DIFFERENCE: Directly finish without showing modal
            // WP Laser doesn't need paint list options - just mark as done
            this.saveData(false);
        },

        reset(resetScannedData = true) {
            if (resetScannedData) {
                this.scannedPleatingListId = null;
            }

            this.pleatingList = null;
        },

        saveData(isolateIntoNewPaintlist = false) {
            this.state.savingData = true;

            pleatingListApi
                .done(this.pleatingList.id, true, isolateIntoNewPaintlist)
                .then((data) => {
                    showInfoMessage(
                        this.trans('pleating_lists.marked_as_done')
                    );
                    this.reset();
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pleating_lists.mark_as_done_failed')
                    );
                })
                .finally(() => {
                    this.state.savingData = false;
                });
        },

        getDataFieldLabel(dataField) {
            const locale = this.currentLocale();

            const translation = dataField.translations.find(
                (translation) => translation.language === locale
            );

            if (translation) {
                return translation.externalName ?? dataField.externalName;
            }

            return dataField.externalName;
        },

        getCreationFilter(product) {
            const valueField = product.valueFields.find((valueField) => {
                return (
                    valueField.dataField.id ===
                    product.productType.pleatingCreationFilter
                );
            });

            if (valueField) {
                return valueField;
            }

            const dataField = product.productType.dataFields.find(
                (dataField) => {
                    return (
                        dataField.id ===
                        product.productType.pleatingCreationFilter
                    );
                }
            );

            if (dataField) {
                return {
                    dataField,
                    value: 0,
                };
            }

            return null;
        },

        getDataFieldLength(product) {
            const valueField = product.valueFields.find((valueField) => {
                return valueField.dataField.name === 'length';
            });

            if (valueField) {
                return valueField;
            }

            const dataField = product.productType.dataFields.find(
                (dataField) => {
                    return dataField.name === 'length';
                }
            );

            if (dataField) {
                return {
                    dataField,
                    value: 0,
                };
            }

            return null;
        },

        getDataFieldUnfoldment(product) {
            const valueField = product.valueFields.find((valueField) => {
                return valueField.dataField.name === 'unfoldment';
            });

            if (valueField) {
                return valueField;
            }

            const dataField = product.productType.dataFields.find(
                (dataField) => {
                    return dataField.name === 'unfoldment';
                }
            );

            if (dataField) {
                return {
                    dataField,
                    value: 0,
                };
            }

            return null;
        },

        openImageModal(product) {
            this.imageModal.product = product;
            this.imageModal.show = true;
        },

        toggleProductFinished(event, product) {
            const checked = event.target.checked || false;

            this.recordsForProduct(product).forEach((record) => {
                record.sawn = checked;
            });

            if (checked) {
                pleatingListApi
                    .markProductRecordsAsSawn(this.pleatingList.id, product.id)
                    .then((response) => {
                        if (response.status !== 200 || !response.data.success) {
                            throw new Error(response.data.code);
                        }

                        showInfoMessage(
                            this.trans('pleating_lists.product_marked_as_sawn')
                        );
                    })
                    .catch((error) => {
                        showErrorMessage(
                            this.trans(
                                'pleating_lists.product_mark_as_sawn_failed'
                            )
                        );
                    });
            } else {
                pleatingListApi
                    .unmarkProductRecordsAsSawn(
                        this.pleatingList.id,
                        product.id
                    )
                    .then((response) => {
                        if (response.status !== 200 || !response.data.success) {
                            throw new Error(response.data.code);
                        }

                        this.pleatingList.done = false;

                        showInfoMessage(
                            this.trans(
                                'pleating_lists.product_unmarked_as_sawn'
                            )
                        );
                    })
                    .catch((error) => {
                        showErrorMessage(
                            this.trans(
                                'pleating_lists.product_unmark_as_sawn_failed'
                            )
                        );
                    });
            }
        },

        isProductFinished(product) {
            return this.recordsForProduct(product).every(
                (record) => record.sawn === true
            );
        },

        recordsForProduct(product) {
            return this.pleatingList.records.filter((record) => {
                return record.sawlines.some(
                    (sawline) => sawline.product.id === product.id
                );
            });
        },
    },
};
</script>
